import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { inquiriesAPI } from '../lib/api';
import { formatDate, truncate } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Loader2, 
  ChevronLeft, 
  ChevronRight,
  MessageSquare,
  Mail,
  Phone,
  Building2,
  Eye,
  EyeOff
} from 'lucide-react';

const Inquiries = () => {
  const { type } = useParams(); // 'product' or 'prasco'
  const isProduct = type === 'product';
  
  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('unread');
  
  // Detail modal
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const pageSize = 10;

  useEffect(() => {
    fetchInquiries();
  }, [type, page, activeTab]);

  const fetchInquiries = useCallback(async () => {
    setIsLoading(true);
    try {
      const isRead = activeTab === 'read' ? true : activeTab === 'unread' ? false : null;
      const fetchFn = isProduct 
        ? inquiriesAPI.getProductInquiries 
        : inquiriesAPI.getPrascoInquiries;
      
      const response = await fetchFn(page, pageSize, isRead);
      setInquiries(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch inquiries');
    } finally {
      setIsLoading(false);
    }
  }, [type, page, activeTab, isProduct]);

  const handleMarkRead = async (inquiry) => {
    try {
      const markFn = isProduct 
        ? inquiriesAPI.markProductInquiryRead 
        : inquiriesAPI.markPrascoInquiryRead;
      
      await markFn(inquiry.id);
      toast.success('Marked as read');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleMarkUnread = async (inquiry) => {
    try {
      const markFn = isProduct 
        ? inquiriesAPI.markProductInquiryUnread 
        : inquiriesAPI.markPrascoInquiryUnread;
      
      await markFn(inquiry.id);
      toast.success('Marked as unread');
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openDetail = (inquiry) => {
    setSelectedInquiry(inquiry);
    setIsDetailOpen(true);
    
    // Auto mark as read when viewing
    if (!inquiry.is_read) {
      handleMarkRead(inquiry);
    }
  };

  return (
    <div className="animate-fade-in" data-testid={`${type}-inquiries-page`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isProduct ? 'bg-orange-50' : 'bg-rose-50'}`}>
            <MessageSquare className={`w-5 h-5 ${isProduct ? 'text-orange-600' : 'text-rose-600'}`} />
          </div>
          <div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {isProduct ? 'Product Inquiries' : 'Prasco Inquiries'}
            </h1>
            <p className="text-sm text-slate-500">{total} total inquiries</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); setPage(1); }}>
        <TabsList className="mb-4">
          <TabsTrigger value="unread" data-testid="tab-unread">
            Unread
          </TabsTrigger>
          <TabsTrigger value="read" data-testid="tab-read">
            Read
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : inquiries.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <MessageSquare className="w-12 h-12 mb-4 opacity-20" />
                <p>No {activeTab} inquiries found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Contact</th>
                        <th>Machine Type</th>
                        <th>Part Name</th>
                        <th>Date</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inquiries.map((inquiry) => (
                        <tr 
                          key={inquiry.id} 
                          className={!inquiry.is_read ? 'bg-amber-50/50' : ''}
                          data-testid={`inquiry-row-${inquiry.id}`}
                        >
                          <td>
                            <span className={`badge ${inquiry.is_read ? 'badge-read' : 'badge-unread'}`}>
                              {inquiry.is_read ? 'Read' : 'Unread'}
                            </span>
                          </td>
                          <td className="font-medium text-slate-900">{inquiry.name}</td>
                          <td>{inquiry.company || '-'}</td>
                          <td>
                            <div className="flex flex-col gap-1 text-xs">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {truncate(inquiry.email, 20)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {inquiry.mobile_number}
                              </span>
                            </div>
                          </td>
                          <td>{inquiry.machine_type || '-'}</td>
                          <td>{inquiry.part_name || '-'}</td>
                          <td className="text-xs">{formatDate(inquiry.created_at)}</td>
                          <td className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDetail(inquiry)}
                                data-testid={`view-btn-${inquiry.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {inquiry.is_read ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkUnread(inquiry)}
                                  data-testid={`mark-unread-btn-${inquiry.id}`}
                                >
                                  <EyeOff className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkRead(inquiry)}
                                  data-testid={`mark-read-btn-${inquiry.id}`}
                                >
                                  <Eye className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                    <p className="text-sm text-slate-500">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg" data-testid="inquiry-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Inquiry Details
            </DialogTitle>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 pb-4 border-b border-slate-200">
                <span className={`badge ${selectedInquiry.is_read ? 'badge-read' : 'badge-unread'}`}>
                  {selectedInquiry.is_read ? 'Read' : 'Unread'}
                </span>
                <span className="text-sm text-slate-500">
                  {formatDate(selectedInquiry.created_at)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Name</p>
                  <p className="font-medium">{selectedInquiry.name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Company</p>
                  <p className="font-medium flex items-center gap-1">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    {selectedInquiry.company || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Email</p>
                  <p className="font-medium flex items-center gap-1">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${selectedInquiry.email}`} className="text-sky-600 hover:underline">
                      {selectedInquiry.email}
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Mobile</p>
                  <p className="font-medium flex items-center gap-1">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a href={`tel:${selectedInquiry.mobile_number}`} className="text-sky-600 hover:underline">
                      {selectedInquiry.mobile_number}
                    </a>
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Product Information</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-400">Machine Type</p>
                    <p className="text-sm">{selectedInquiry.machine_type || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Manufacturer</p>
                    <p className="text-sm">{selectedInquiry.manufacturer || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Model</p>
                    <p className="text-sm">{selectedInquiry.model || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Part Name</p>
                    <p className="text-sm">{selectedInquiry.part_name || '-'}</p>
                  </div>
                </div>
              </div>

              {selectedInquiry.comments && (
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Comments</p>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                    {selectedInquiry.comments}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                {selectedInquiry.is_read ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleMarkUnread(selectedInquiry);
                      setIsDetailOpen(false);
                    }}
                  >
                    <EyeOff className="w-4 h-4 mr-2" />
                    Mark as Unread
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      handleMarkRead(selectedInquiry);
                      setIsDetailOpen(false);
                    }}
                    className="bg-slate-900 hover:bg-slate-800"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Mark as Read
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inquiries;
